import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faExpand,
  faXmark,
  faGear,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { formatTriageDetected } from "../../utils/careTriageModel";
import "./TriageReviewDialogue.css";

export function isSupervisorUser(user) {
  const role = String(user?.role || user?.highestRole || "").toLowerCase();
  return ["admin", "superadmin", "owner", "manager", "dps", "supervisor"].some((r) =>
    role.includes(r),
  );
}

const NURSE_TYPES = [
  { id: "update", label: "Update" },
  { id: "ready_for_review", label: "Ready for Review" },
];

const SUPERVISOR_TYPES = [
  { id: "feedback", label: "Feedback / Update" },
  { id: "follow_up_needed", label: "Follow-Up Needed" },
  { id: "approve_close", label: "Approve & Close" },
];

const STATUS_CHIP = {
  ready_for_review: { label: "Ready for Review", tone: "green" },
  follow_up_needed: { label: "Follow-Up Needed", tone: "amber" },
  approved_closed: { label: "Approved & Closed", tone: "green" },
  reopened: { label: "Reopened", tone: "amber" },
};

const TYPE_CHIP = {
  update: { label: "Update", tone: "blue" },
  feedback: { label: "Feedback", tone: "blue" },
};

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

function MessageEntry({ m }) {
  const isSystem = !m.userName || m.messageType === "system";
  const chip = STATUS_CHIP[m.reviewStatusChange] || TYPE_CHIP[m.messageType];
  return (
    <div className={`triage-dialogue__entry${isSystem ? " is-system" : ""}`}>
      <span className="triage-dialogue__avatar" aria-hidden="true">
        {isSystem ? <FontAwesomeIcon icon={faGear} /> : initials(m.userName)}
      </span>
      <div className="triage-dialogue__body">
        <div className="triage-dialogue__meta">
          <strong>{isSystem ? "System" : m.userName}</strong>
          {m.role && !isSystem ? <span>· {m.role}</span> : null}
          <span>· {formatTriageDetected(m.at)}</span>
          {chip ? (
            <span className={`triage-dialogue__chip triage-dialogue__chip--${chip.tone}`}>
              {chip.label}
            </span>
          ) : null}
        </div>
        <p>{m.text}</p>
      </div>
    </div>
  );
}

function Feed({ messages, tall = false }) {
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages.length]);
  return (
    <div
      className={`triage-dialogue__feed${tall ? " triage-dialogue__feed--tall" : ""}`}
      role="log"
      aria-live="polite"
    >
      {messages.length === 0 ? (
        <p className="triage-dialogue__empty">No review messages yet.</p>
      ) : (
        messages.map((m) => <MessageEntry key={m._id || `${m.at}-${m.userName}`} m={m} />)
      )}
      <div ref={bottomRef} />
    </div>
  );
}

export default function TriageReviewDialogue({
  row,
  currentUser,
  readOnly = false,
  onSubmitMessage,
  onRequestClose,
  onRequestReopen,
}) {
  const supervisor = isSupervisorUser(currentUser);
  const types = (supervisor ? SUPERVISOR_TYPES : NURSE_TYPES).filter(
    (t) => t.id !== "approve_close" || onRequestClose,
  );
  const [messageType, setMessageType] = useState(types[0].id);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  const messages = row?.messages || [];
  const isApprove = messageType === "approve_close";
  const hasUnread = supervisor ? row?.unreadForSupervisor : row?.unreadForAssignee;

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => e.key === "Escape" && setExpanded(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (isApprove) {
      onRequestClose?.(row, text.trim());
      return;
    }
    if (!text.trim() || !onSubmitMessage) return;
    setSubmitting(true);
    try {
      await onSubmitMessage(row, messageType, text.trim());
      setText("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!reopenReason.trim()) return;
    setSubmitting(true);
    try {
      await onRequestReopen?.(row, reopenReason.trim());
      setReopenOpen(false);
      setReopenReason("");
    } finally {
      setSubmitting(false);
    }
  };

  const composer = !readOnly ? (
    <form className="triage-dialogue__composer" onSubmit={handleSubmit}>
      <input
        type="text"
        className="triage-dialogue__input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={isApprove ? "Final review note (optional)…" : "Message / update…"}
        aria-label="Message"
      />
      <select
        className="triage-dialogue__type-select"
        value={messageType}
        onChange={(e) => setMessageType(e.target.value)}
        aria-label="Message type"
      >
        {types.map((t) => (
          <option key={t.id} value={t.id}>
            {t.label}
          </option>
        ))}
      </select>
      <button
        type="submit"
        className={`triage-dialogue__submit${isApprove ? " triage-dialogue__submit--approve" : ""}`}
        disabled={submitting || (!isApprove && !text.trim())}
      >
        {isApprove ? "Review & Close" : "Submit"}
      </button>
    </form>
  ) : supervisor && onRequestReopen ? (
    <div className="triage-dialogue__reopen-box">
      {!reopenOpen ? (
        <button type="button" className="triage-dialogue__reopen" onClick={() => setReopenOpen(true)}>
          <FontAwesomeIcon icon={faRotateLeft} /> Reopen Case
        </button>
      ) : (
        <>
          <label className="triage-dialogue__reopen-label" htmlFor={`reopen-${row?.id}`}>
            Reason for reopening <span aria-hidden="true">*</span>
          </label>
          <textarea
            id={`reopen-${row?.id}`}
            rows={2}
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            placeholder="New information received, closed prematurely…"
          />
          <div className="triage-dialogue__reopen-actions">
            <button type="button" className="triage-dialogue__ghost" onClick={() => setReopenOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="triage-dialogue__submit"
              disabled={submitting || !reopenReason.trim()}
              onClick={handleReopen}
            >
              Reopen Case
            </button>
          </div>
        </>
      )}
    </div>
  ) : null;

  const title = readOnly ? "Review History / Dialogue (Final Record)" : "Review History / Dialogue";

  return (
    <div className="triage-dialogue">
      <div className="triage-dialogue__head">
        <h4>
          <FontAwesomeIcon icon={faComments} /> {title}
        </h4>
        {hasUnread && !readOnly ? <span className="triage-dialogue__badge">New</span> : null}
        <button
          type="button"
          className="triage-dialogue__icon-btn"
          onClick={() => setExpanded(true)}
          aria-label="Expand review history"
          title="Expand"
        >
          <FontAwesomeIcon icon={faExpand} />
        </button>
      </div>

      <Feed messages={messages} />
      {composer}

      {expanded ? (
        <div className="triage-dialogue-drawer-backdrop" role="presentation" onClick={() => setExpanded(false)}>
          <aside
            className="triage-dialogue-drawer"
            role="dialog"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="triage-dialogue-drawer__head">
              <div>
                <h3>{title}</h3>
                <p>
                  {row?.clientName} · {row?.note}
                </p>
              </div>
              <button
                type="button"
                className="triage-dialogue__icon-btn"
                onClick={() => setExpanded(false)}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <Feed messages={messages} tall />
            {composer}
          </aside>
        </div>
      ) : null}
    </div>
  );
}
